import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';

import PersonalAccessTokensList from './PersonalAccessTokensList';
import { SettingsSection } from '../common';
import { PersonalAccessTokenForm } from './PersonalAccessTokenForm';
import { RootState } from '../../../reducers';
import {
  createToken,
  listPermissions,
  revokeToken,
  rotateToken,
  listTokens,
} from './thunk';
import VisibleToken from './VisibleToken';

export default function PersonalAccessTokensManager() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiSettings = useSelector((state: RootState) => state.settings.api);

  const [isTokenFormOpen, setIsTokenFormOpen] = useState(false);

  useEffect(() => {
    dispatch(listPermissions());
    dispatch(listTokens());
  }, [dispatch]);

  return (
    <SettingsSection
      title={t('settings.api.personalAccessTokensManager.title')}
      badge={t('settings.api.personalAccessTokensManager.badge')}
    >
      <p className="text-sm text-muted-foreground mb-2">
        {t('settings.api.personalAccessTokensManager.description.intro')}
      </p>
      <p className="text-sm text-muted-foreground mb-2">
        {t('settings.api.personalAccessTokensManager.description.usage')}
      </p>
      <pre className="mt-2 p-3 bg-muted rounded text-sm">
        <code>
          {t(
            'settings.api.personalAccessTokensManager.headers.xScreenappToken',
          )}
        </code>
      </pre>
      <p className="text-sm text-muted-foreground my-2">
        {t('settings.api.personalAccessTokensManager.description.or')}
      </p>
      <pre className="mt-2 p-3 bg-muted rounded text-sm">
        <code>
          {t('settings.api.personalAccessTokensManager.headers.authorization')}
        </code>
      </pre>

      {Object.keys(apiSettings.tokens.visible).map((tokenId) => (
        <VisibleToken key={tokenId} tokenId={tokenId} />
      ))}

      {isTokenFormOpen && (
        <PersonalAccessTokenForm
          permissions={apiSettings.permissions.available}
          onCancel={() => setIsTokenFormOpen(false)}
          onSubmit={(args) => {
            dispatch(createToken(args));
            setIsTokenFormOpen(false);
          }}
        />
      )}
      <div className="flex flex-row justify-between items-center my-4">
        <h3 className="text-lg font-medium">
          {t('settings.api.personalAccessTokensManager.activeTokens.title')}
        </h3>
        {!isTokenFormOpen && (
          <Button variant="default" onClick={() => setIsTokenFormOpen(true)}>
            {t(
              'settings.api.personalAccessTokensManager.activeTokens.addButton',
            )}
          </Button>
        )}
      </div>

      <PersonalAccessTokensList
        tokens={apiSettings.tokens.active.items}
        onRevoke={(token) => dispatch(revokeToken(token._id))}
        onRotate={(token) => dispatch(rotateToken(token._id))}
      />
    </SettingsSection>
  );
}
